import { SupabaseClient } from "@supabase/supabase-js";
import { logException, SentryFeatureTags } from "@punchup/sentry";
import { Database } from "../../supabase.types";

/**
 * What the knowledge base is missing, from what the assistant saw: escalations
 * nobody turned into a case, answers rated down, searches that found nothing,
 * answers that cited nothing, and how often each case is cited.
 */

type Client = SupabaseClient<Database>;

function fail(action: string, error: unknown): never {
  logException(error, {
    tags: { feature: SentryFeatureTags.SupportAssistant, action },
  });
  throw error;
}

export async function listEscalatedConversationsWithoutCase(
  supabase: Client,
  options: { since: string; limit: number }
) {
  const { data, error } = await supabase
    .from("support_conversations")
    .select("id, title, escalation_note, status_changed_at")
    .eq("status", "escalated")
    .is("resulting_case_id", null)
    .gte("status_changed_at", options.since)
    .order("status_changed_at", { ascending: false })
    .limit(options.limit);

  if (error) fail("listEscalatedConversationsWithoutCase", error);
  return data ?? [];
}

export async function listThumbsDownFeedback(
  supabase: Client,
  options: { since: string; limit: number }
) {
  const { data, error } = await supabase
    .from("support_answer_feedback")
    .select(
      "reason, comment, created_at, message:support_messages(id, conversation_id, turn_id)"
    )
    .eq("rating", "down")
    .gte("created_at", options.since)
    .order("created_at", { ascending: false })
    .limit(options.limit);

  if (error) fail("listThumbsDownFeedback", error);
  return data ?? [];
}

export async function listZeroResultSearches(
  supabase: Client,
  options: { since: string; limit: number }
) {
  const { data, error } = await supabase
    .from("support_search_events")
    .select("query, category, conversation_id, created_at")
    .eq("result_case_ids", "{}")
    .gte("created_at", options.since)
    .order("created_at", { ascending: false })
    .limit(options.limit);

  if (error) fail("listZeroResultSearches", error);
  return data ?? [];
}

/** Answers since `since` with the cases each cited (empty for none). */
export async function listSupportAnswers(
  supabase: Client,
  options: { since: string; limit: number }
) {
  const { data, error } = await supabase
    .from("support_messages")
    .select("id, conversation_id, turn_id, cited_case_ids, created_at")
    .eq("kind", "answer")
    .gte("created_at", options.since)
    .order("created_at", { ascending: false })
    .limit(options.limit);

  if (error) fail("listSupportAnswers", error);
  return data ?? [];
}

/** The question text of each turn, by turn id. */
export async function getSupportTurnQuestions(
  supabase: Client,
  turnIds: string[]
): Promise<Map<string, string>> {
  if (turnIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from("support_messages")
    .select("turn_id, content")
    .eq("kind", "question")
    .in("turn_id", turnIds);

  if (error) fail("getSupportTurnQuestions", error);
  return new Map(
    (data ?? []).map((row) => {
      const [block] = row.content as { text?: string }[];
      return [row.turn_id, block?.text ?? ""];
    })
  );
}
